/** True when a line is a real screening question, not a list index or tag stub. */
export function isValidPromptLine(text: string): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  if (/^\d+$/.test(t)) return false;
  if (t.length < 12) return false;
  if (/^\[?\s*(MOST\s+RECOMMENDED|RECOMMENDED)\s*\]?\s*$/i.test(t)) {
    return false;
  }
  return true;
}

/** Strip leading list numbers and recommendation tags from one line. */
export function normalizePromptLine(line: string): string {
  let text = (line || '').trim();
  text = text.replace(/^\s*\d+[.)\-:]?\s*/, '');
  text = text.replace(/^\[?\s*MOST\s+RECOMMENDED\s*\]?\s*[:\-–]?\s*/i, '');
  text = text.replace(/^\[?\s*RECOMMENDED\s*\]?\s*[:\-–]?\s*/i, '');
  return text.trim();
}

function extractPromptFromItem(item: unknown): string | null {
  if (typeof item === 'string') {
    const text = normalizePromptLine(item);
    return isValidPromptLine(text) ? text : null;
  }
  if (item && typeof item === 'object') {
    const row = item as Record<string, unknown>;
    for (const key of ['question', 'text', 'prompt', 'content']) {
      if (typeof row[key] === 'string') {
        const text = normalizePromptLine(row[key] as string);
        if (isValidPromptLine(text)) return text;
      }
    }
  }
  return null;
}

/** Normalize AI / autofill text into a clean list of prompt strings. */
export function parseAiPromptList(answer: unknown, limit?: number): string[] {
  const collected: string[] = [];

  const pushLine = (line: string) => {
    const text = normalizePromptLine(line);
    if (isValidPromptLine(text)) {
      collected.push(text);
    }
  };

  if (Array.isArray(answer)) {
    for (const item of answer) {
      const extracted = extractPromptFromItem(item);
      if (extracted) {
        collected.push(extracted);
      } else if (typeof item === 'string') {
        pushLine(item);
      }
    }
  } else if (typeof answer === 'string') {
    answer.split(/\r?\n+/).forEach(pushLine);
  } else if (answer && typeof answer === 'object') {
    const obj = answer as Record<string, unknown>;
    const questions = obj.questions;
    if (Array.isArray(questions)) {
      for (const item of questions) {
        const extracted = extractPromptFromItem(item);
        if (extracted) collected.push(extracted);
      }
    } else if (typeof questions === 'string') {
      questions.split(/\r?\n+/).forEach(pushLine);
    } else {
      for (const value of Object.values(obj)) {
        const extracted = extractPromptFromItem(value);
        if (extracted) {
          collected.push(extracted);
        } else if (typeof value === 'string') {
          pushLine(value);
        }
      }
    }
  }

  const unique: string[] = [];
  const seen = new Set<string>();
  for (const prompt of collected) {
    const key = prompt.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(prompt);
  }

  if (typeof limit === 'number' && limit > 0) {
    return unique.slice(0, limit);
  }

  return unique;
}
